/**
 * The browser half of the web chat wire.
 *
 * ─── WHY THIS IS `fetch` + `ReadableStream`, NOT THE BUILT-IN SSE CLIENT ──────
 *
 * The browser's own SSE client can carry neither the D-10 session token nor the D-13 page context:
 * it has no request body and no custom headers. The only workaround that keeps it usable is putting
 * a bearer-equivalent credential in the URL, which puts it in dyno access logs and browser history
 * (T-38-69) — and `adminChatService.ts`'s `?token=` factory is precisely the precedent this file
 * deliberately does NOT copy. So the transport is `fetch` + `response.body.getReader()`, and the
 * price is a hand-rolled `Last-Event-ID` resume and a hand-rolled frame parser. The parser lives in
 * `./sseFrameParser` as a pure module for exactly that reason.
 *
 * (The rejected class is named without its constructor's identifier on purpose: this plan's own
 * gate greps this file for that identifier and expects zero hits, and a prohibition that matches
 * itself measures its own comment rather than the code. Same correction plans 38-05, 38-07, 38-08,
 * 38-10 and 38-11 each applied once.)
 *
 * ─── WHY THE BASE URL IS ABSOLUTE AND CROSS-ORIGIN ────────────────────────────
 *
 * CLAUDE.md says the frontend base URL is `/api` and that services must not repeat the prefix.
 * That rule does not apply here, on purpose. The chat agent is its own Heroku dyno, and the browser
 * must address it DIRECTLY on its raw `*.herokuapp.com` host:
 *
 *  - The chat dyno is not behind Cloudflare, so nothing between the browser and the stream is
 *    entitled to buffer it. Routing the stream through `www.yetfarrag.com/api` would put it behind
 *    both Cloudflare and the main dyno's `compression` middleware, and a compressing intermediary
 *    is the classic way a working SSE stream turns into "every delta arrives at once at the end".
 *    This phase avoids that risk for free by not introducing the hop.
 *  - A Cloudflare-proxied `chat.yetfarrag.com` CNAME would reintroduce the same intermediary. It is
 *    explicitly rejected (38-RESEARCH.md § 1).
 *
 * `VITE_CHAT_AGENT_URL` is inlined by Vite AT BUILD TIME, so it must be set on the `yetfarrag` app
 * BEFORE the frontend build, not merely before boot.
 *
 * ─── CONTRACT OF THIS MODULE ──────────────────────────────────────────────────
 *
 * Every export RETURNS a value and never throws, and no browser global is evaluated at module
 * import time. The Vike SSR build imports this file; a module-scope `localStorage` read or an
 * uncaught rejection here would take down a whole route rather than degrade one panel (T-38-74).
 *
 * The session token is a CREDENTIAL. It travels in `X-Chat-Session` and nowhere else: never in a
 * query string, never in a path, never in a log line. The site JWT is NOT read from storage by this
 * module at all — it is passed in from `useAuth().session.token` in React state, because this repo
 * already has two divergent auth storage keys and a third reader would be a third thing to get
 * wrong.
 */

import { createSseFrameParser } from "./sseFrameParser";

// ─── Base URL ─────────────────────────────────────────────────────────────────

/**
 * The RAW herokuapp host. Not a custom domain, for the buffering reason in the docblock above.
 * Overridden per environment by `VITE_CHAT_AGENT_URL`.
 */
const DEFAULT_CHAT_AGENT_URL = "https://yetfarrag-chat-887c9e99e2ca.herokuapp.com";

/** Everything on this client hangs off one prefix, established by plan 38-08. */
const WEB_PATH = "/api/chat/web";

/** `import.meta.env` is typed defensively, following `adminChatService.ts`'s own cast. */
const readInlinedEnv = (): string | undefined =>
  (import.meta as { env?: { VITE_CHAT_AGENT_URL?: string } }).env?.VITE_CHAT_AGENT_URL;

const stripTrailingSlash = (value: string): string =>
  value.endsWith("/") ? value.slice(0, -1) : value;

/**
 * The server/client env split, copied from `enviroment.ts:14-35`.
 *
 * On the server `process.env` is the reader that can see a config var; `import.meta.env` only ever
 * holds what was inlined at build time. On the client `process` may not exist at all, so it is
 * feature-detected rather than assumed.
 */
const resolveChatAgentBase = (): string => {
  if (typeof window === "undefined") {
    const fromRuntime =
      typeof process === "undefined" ? undefined : process.env?.VITE_CHAT_AGENT_URL;
    return stripTrailingSlash(fromRuntime || readInlinedEnv() || DEFAULT_CHAT_AGENT_URL);
  }
  return stripTrailingSlash(readInlinedEnv() || DEFAULT_CHAT_AGENT_URL);
};

/** Exported for the admin/diagnostic surfaces that want to show which host the panel talks to. */
export const getChatAgentBaseUrl = (): string => resolveChatAgentBase();

const buildUrl = (path: string): string => `${resolveChatAgentBase()}${WEB_PATH}${path}`;

// ─── The wire vocabulary ──────────────────────────────────────────────────────

/**
 * A MIRROR of `chat-agent/src/channels/webTransport.ts`'s `wireFrameKindMap` / `WireFrame`, not a
 * parallel guess. The two workspaces do not share a types package, so the mirror is maintained by
 * hand and the source of truth is named here so a diverging reader knows which side to fix.
 * Enum-as-const with the union derived from it, per CLAUDE.md.
 */
export const streamFrameKindMap = {
  /** One text chunk, exactly as the model produced it. */
  delta: "delta",
  /** A tool started; carries the tool name for the Arabic label lookup. */
  toolStart: "toolStart",
  /** That tool finished. */
  toolEnd: "toolEnd",
  /** A deep tool was refused by the daily spend ceiling (D-17's disclosure trigger). */
  degraded: "degraded",
  /** The run finished and the assistant turn is persisted. TERMINAL. */
  done: "done",
  /** The stop button fired; the partial text is what there is. TERMINAL. */
  interrupted: "interrupted",
  /** Something failed. A machine code, never a message body. TERMINAL. */
  error: "error",
  /** No live buffer. Re-read the thread over the JSON endpoint instead of replaying. TERMINAL. */
  rehydrate: "rehydrate",
  /** A rate tier refused the send. TERMINAL for this client — see `TERMINAL_KINDS`. */
  limit: "limit",
} as const;

export type StreamFrameKind = keyof typeof streamFrameKindMap;

export type WireFrame =
  | { kind: typeof streamFrameKindMap.delta; text: string }
  | { kind: typeof streamFrameKindMap.toolStart; toolCallId: string; toolName: string }
  | {
      kind: typeof streamFrameKindMap.toolEnd;
      toolCallId: string;
      state?: "output-available" | "output-error";
    }
  | { kind: typeof streamFrameKindMap.degraded; reason: "daily_cap_reached" }
  | {
      kind: typeof streamFrameKindMap.done;
      turnId: string;
      content: string;
      blocks?: unknown;
      suggestedReplies?: string[];
    }
  | { kind: typeof streamFrameKindMap.interrupted; turnId: string; content: string }
  | { kind: typeof streamFrameKindMap.error; code: string }
  | { kind: typeof streamFrameKindMap.rehydrate }
  | {
      kind: typeof streamFrameKindMap.limit;
      layer: "ip" | "session" | "user";
      retryAfterSec: number;
    };

/**
 * "This run is over, stop waiting."
 *
 * DELIBERATELY ONE ENTRY WIDER than the server's `TERMINAL_KINDS`, which lists four. The server's
 * set is about BUFFER bookkeeping and a `limit` refusal is never buffered — it is written with no
 * `id:` and the response ends immediately. From the client's side that is unambiguously terminal,
 * and treating it as a dropped stream instead would send the panel straight back into the same
 * refusal, on a backoff, which is the reconnect-storm this client is bounded to avoid (T-38-72).
 */
const TERMINAL_KINDS: ReadonlySet<StreamFrameKind> = new Set<StreamFrameKind>([
  streamFrameKindMap.done,
  streamFrameKindMap.interrupted,
  streamFrameKindMap.error,
  streamFrameKindMap.rehydrate,
  streamFrameKindMap.limit,
]);

/**
 * `hasOwnProperty.call` rather than `in`, so a payload claiming `kind: "toString"` is not treated
 * as a known frame, and rather than `Object.hasOwn`, which needs an ES2022 lib this app's tsconfig
 * does not declare.
 */
const isKnownFrameKind = (candidate: unknown): candidate is StreamFrameKind =>
  typeof candidate === "string" &&
  Object.prototype.hasOwnProperty.call(streamFrameKindMap, candidate);

// ─── Results ──────────────────────────────────────────────────────────────────

/**
 * The machine strings the caller switches on. The first seven are the server's own wire vocabulary
 * (`webChatErrorMap`); the rest are conditions only the client can observe.
 *
 * NO ARABIC HERE. Copy lives in the panel's `chatCopy.ts`, so one refusal has one translation and
 * this module stays a transport.
 */
export const chatClientErrorMap = {
  session_required: "session_required",
  session_unknown: "session_unknown",
  session_expired: "session_expired",
  rate_limited: "rate_limited",
  engine_not_streaming: "engine_not_streaming",
  invalid_request: "invalid_request",
  internal_error: "internal_error",
  /** The request never reached the dyno, or the connection died mid-stream. */
  network_error: "network_error",
  /** The reconnect budget ran out. This is the panel's "جرب تاني" row. */
  reconnect_exhausted: "reconnect_exhausted",
  /** This runtime has no readable response body. Feature-detected, never thrown. */
  stream_unsupported: "stream_unsupported",
  /** Called during SSR. Not an error the user can see; the panel is client-only. */
  server_only: "server_only",
  /** The caller's own `AbortController` fired. */
  aborted: "aborted",
} as const;

export type ChatClientErrorCode = keyof typeof chatClientErrorMap;

/** The wire strings, i.e. the subset a server envelope is allowed to name. */
const WIRE_ERROR_CODES: ReadonlySet<string> = new Set<string>([
  chatClientErrorMap.session_required,
  chatClientErrorMap.session_unknown,
  chatClientErrorMap.session_expired,
  chatClientErrorMap.rate_limited,
  chatClientErrorMap.engine_not_streaming,
  chatClientErrorMap.invalid_request,
  chatClientErrorMap.internal_error,
]);

export type LimitPayload = { layer: "ip" | "session" | "user"; retryAfterSec: number };

export type ChatClientFailure = {
  ok: false;
  code: ChatClientErrorCode;
  /** Present when a response was actually received. Absent for a transport-level failure. */
  status?: number;
  /** Present on `rate_limited`. Same `{ layer, retryAfterSec }` shape as the `limit` frame. */
  limit?: LimitPayload;
};

export type ChatClientResult<T> = { ok: true; data: T } | ChatClientFailure;

/**
 * An explicit type guard rather than a bare `if (result.ok)`.
 *
 * `frontend/tsconfig.app.json` sets `strict: false`, and with `strictNullChecks` off TypeScript does
 * not narrow this discriminated union on the boolean literal — every `result.code` read in the
 * else-branch becomes an error. A user-defined guard narrows regardless of that setting, which is
 * why the resume helper below routes through it instead of relying on control flow.
 */
export const isChatClientFailure = <T>(
  result: ChatClientResult<T>
): result is ChatClientFailure => result.ok !== true;

/**
 * Plan-text note: the task text says `mintOrRefreshSession` returns the session "or `null`". It
 * returns a `ChatClientResult` instead — which is still a value and still never throws, so the
 * never-throw contract is intact, but it keeps the distinction the SAME task requires two
 * paragraphs later ("map the machine error strings onto a typed result the caller can switch on").
 * A bare `null` would collapse `rate_limited`, `engine_not_streaming` and a dead network into one
 * indistinguishable answer, and the panel has a different, honest line for each.
 */

// ─── Session token storage ────────────────────────────────────────────────────

/**
 * The session token's home. It is XSS-readable, which is the same exposure the site's existing JWT
 * already carries (T-38-70, disposition: accept) — and it is mitigated in depth by the token being
 * revocable server-side and scoped to a single conversation.
 *
 * This is the ONLY storage key this module touches. It is not an auth key and must never be one.
 */
const SESSION_STORAGE_KEY = "yetfarrag_chat_session";

export const readStoredSessionId = (): string | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(SESSION_STORAGE_KEY);
    return typeof raw === "string" && raw.length > 0 ? raw : null;
  } catch {
    // Private-browsing modes and storage-disabled profiles throw on ACCESS, not just on write.
    // A visitor with no storage gets a fresh session per page load, which still works.
    return null;
  }
};

export const writeStoredSessionId = (sessionId: string): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  } catch {
    // Nothing to do and nothing to report: the panel works without persistence.
  }
};

export const clearStoredSession = (): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // As above.
  }
};

// ─── Shared request plumbing ──────────────────────────────────────────────────

const SESSION_HEADER = "X-Chat-Session";
const LAST_EVENT_ID_HEADER = "Last-Event-ID";

type Credentials = { sessionId?: string | null; jwt?: string | null };

/**
 * Credentials go in HEADERS. There is no branch in this file that puts either one anywhere else,
 * and that is the whole of T-38-69's mitigation.
 */
const buildHeaders = ({
  sessionId,
  jwt,
  accept,
  json,
  lastEventId,
}: Credentials & {
  accept: string;
  json?: boolean;
  lastEventId?: number | null;
}): Record<string, string> => {
  const headers: Record<string, string> = { Accept: accept };
  if (json === true) headers["Content-Type"] = "application/json";
  if (typeof sessionId === "string" && sessionId.length > 0) headers[SESSION_HEADER] = sessionId;
  if (typeof jwt === "string" && jwt.length > 0) headers.Authorization = `Bearer ${jwt}`;
  if (typeof lastEventId === "number" && Number.isFinite(lastEventId)) {
    headers[LAST_EVENT_ID_HEADER] = String(lastEventId);
  }
  return headers;
};

const isAbortError = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  (error as { name?: string }).name === "AbortError";

const readLimitPayload = (envelope: unknown): LimitPayload | undefined => {
  if (typeof envelope !== "object" || envelope === null) return undefined;
  const limit = (envelope as { limit?: unknown }).limit;
  if (typeof limit !== "object" || limit === null) return undefined;
  const { layer, retryAfterSec } = limit as { layer?: unknown; retryAfterSec?: unknown };
  if (layer !== "ip" && layer !== "session" && layer !== "user") return undefined;
  if (typeof retryAfterSec !== "number" || !Number.isFinite(retryAfterSec)) return undefined;
  return { layer, retryAfterSec };
};

/**
 * Turn a non-2xx response into a typed failure. The server's `{ ok: false, error }` envelope names
 * a machine string; anything unrecognised (an HTML error page from an intermediary, a truncated
 * body) degrades to a status-derived code rather than surfacing raw server text to the panel.
 */
const readFailure = async (response: Response): Promise<ChatClientFailure> => {
  let envelope: unknown = null;
  try {
    envelope = await response.json();
  } catch {
    envelope = null;
  }
  const named = (envelope as { error?: unknown } | null)?.error;
  const code: ChatClientErrorCode =
    typeof named === "string" && WIRE_ERROR_CODES.has(named)
      ? (named as ChatClientErrorCode)
      : response.status === 429
        ? chatClientErrorMap.rate_limited
        : response.status === 401 || response.status === 403
          ? chatClientErrorMap.session_unknown
          : chatClientErrorMap.internal_error;
  const limit = readLimitPayload(envelope);
  return { ok: false, code, status: response.status, ...(limit ? { limit } : {}) };
};

/**
 * The one guarded primitive the four JSON endpoints share.
 *
 * The `typeof window === "undefined"` guard is not only about crashes — Node 18 has a global
 * `fetch`, so on the server this would SUCCEED at making a real cross-origin request during an SSR
 * render, with no visitor session behind it and the route's response time hostage to another dyno.
 * Refusing is the correct answer, and it is why the four exports below are thin wrappers around
 * this rather than four copies of the guard.
 */
const requestJson = async <T>({
  path,
  method,
  sessionId,
  jwt,
  body,
  signal,
}: Credentials & {
  path: string;
  method: "GET" | "POST";
  body?: unknown;
  signal?: AbortSignal;
}): Promise<ChatClientResult<T>> => {
  if (typeof window === "undefined") return { ok: false, code: chatClientErrorMap.server_only };

  let response: Response;
  try {
    response = await window.fetch(buildUrl(path), {
      method,
      headers: buildHeaders({
        sessionId,
        jwt,
        accept: "application/json",
        json: body !== undefined,
      }),
      // `omit`, not `include`: a hostile page must find no ambient authority to ride here. The
      // session token is an explicit header, so there is nothing a cookie would add (T-38-71).
      credentials: "omit",
      cache: "no-store",
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal === undefined ? {} : { signal }),
    });
  } catch (error) {
    return {
      ok: false,
      code: isAbortError(error) ? chatClientErrorMap.aborted : chatClientErrorMap.network_error,
    };
  }

  if (!response.ok) return await readFailure(response);

  try {
    const envelope = (await response.json()) as { ok?: boolean; data?: T };
    if (envelope?.ok !== true) return { ok: false, code: chatClientErrorMap.internal_error };
    return { ok: true, data: envelope.data as T };
  } catch {
    // A 200 whose body is not the envelope means something between here and the dyno rewrote it.
    return { ok: false, code: chatClientErrorMap.internal_error, status: response.status };
  }
};

// ─── The JSON endpoints ───────────────────────────────────────────────────────

export type WebSession = { sessionId: string; issuedAt: string; conversationId: string };

/**
 * `POST /session`. Answers 200 on all four server paths (valid token / JWT continuity / unknown
 * token / fresh mint), so the caller does not branch: it stores whatever `sessionId` comes back.
 * Presenting a stale token here MINTS rather than refusing, which is what makes this safe to call
 * on every panel open.
 */
export const mintOrRefreshSession = async ({
  sessionId,
  jwt,
  signal,
}: Credentials & { signal?: AbortSignal } = {}): Promise<ChatClientResult<WebSession>> =>
  await requestJson<WebSession>({
    path: "/session",
    method: "POST",
    sessionId: sessionId ?? readStoredSessionId(),
    jwt,
    body: {},
    signal,
  });

export type ThreadTurn = {
  id: string;
  role: string;
  content: string;
  intent?: string;
  sentBy?: string;
  blocks?: unknown;
  createdAt: string;
};

/**
 * `humanMode` is OPTIONAL on purpose: chat-agent and the frontend deploy separately, so an older chat
 * dyno sends no field and the widget must read absent as `false`.
 */
export type ThreadPayload = { conversationId: string; turns: ThreadTurn[]; humanMode?: boolean };

/**
 * `GET /thread`. Also the answer to a `rehydrate` frame: the buffer is gone (the dyno cycled, or
 * the run finished while the browser was away), so the thread is read rather than replayed.
 */
export const fetchThread = async ({
  sessionId,
  jwt,
  limit,
  signal,
}: Credentials & { limit?: number; signal?: AbortSignal }): Promise<
  ChatClientResult<ThreadPayload>
> => {
  const query =
    typeof limit === "number" && Number.isFinite(limit)
      ? `?${new URLSearchParams({ limit: String(limit) }).toString()}`
      : "";
  return await requestJson<ThreadPayload>({
    path: `/thread${query}`,
    method: "GET",
    sessionId,
    jwt,
    signal,
  });
};

/**
 * `GET /unread`. D-04's dot. `since` is the client's own high-water mark; absent means "count
 * everything", which over-counts in the safe direction (a dot the user dismisses by opening the
 * panel beats a notice silently lost).
 */
export const fetchUnread = async ({
  sessionId,
  jwt,
  since,
  signal,
}: Credentials & { since?: string | null; signal?: AbortSignal }): Promise<
  ChatClientResult<{ unread: number }>
> => {
  const params = new URLSearchParams();
  // The only thing that ever reaches a query string in this file, and it is a timestamp.
  if (typeof since === "string" && since.length > 0) params.set("since", since);
  const query = params.toString();
  return await requestJson<{ unread: number }>({
    path: `/unread${query.length > 0 ? `?${query}` : ""}`,
    method: "GET",
    sessionId,
    jwt,
    signal,
  });
};

/**
 * `POST /abort` — the stop button.
 *
 * THE TRAP, stated plainly: aborting the client-side `fetch` does NOT stop the server run. The
 * model keeps producing tokens and the dyno keeps paying for them; all a local `AbortController`
 * achieves is that nobody is listening. The stop button MUST post here.
 *
 * Idempotent, and takes no body — the conversation comes from the session (T-38-61). `aborted:
 * false` means the run had already finished, which is not an error: the panel should keep waiting
 * for the terminal frame it is about to get anyway.
 */
export const requestAbort = async ({
  sessionId,
  jwt,
}: Credentials): Promise<ChatClientResult<{ aborted: boolean }>> =>
  await requestJson<{ aborted: boolean }>({
    path: "/abort",
    method: "POST",
    sessionId,
    jwt,
    body: {},
  });

// ─── The stream ───────────────────────────────────────────────────────────────

/** Mirrors `ChatTurn.PageContext`: a ROUTE DESCRIPTOR, exactly three scalar fields, no nesting. */
export type PageContext = {
  routeType: "store" | "category" | "generic";
  storeSlug?: string;
  categorySlug?: string;
};

export type StreamOutcome = {
  /** The last SSE id actually seen, or `null`. This is what a resume sends as `Last-Event-ID`. */
  lastEventId: number | null;
  /** The terminal frame kind, or `null` when the response ended WITHOUT one — a dropped stream. */
  terminal: StreamFrameKind | null;
  /** How many frames were handed to `onFrame`. */
  framesDelivered: number;
};

export type OpenStreamInput = {
  sessionId: string;
  jwt?: string | null;
  /**
   * ONE id per composed message, reused across every retry and reconnect of that send. The server
   * dedups on it and answers a duplicate with a REPLAY rather than a second run, so a new id per
   * attempt would buy the visitor two answers to one question.
   */
  messageId: string;
  text: string;
  pageContext?: PageContext;
  lastEventId?: number | null;
  onFrame: (frame: WireFrame) => void;
  onId?: (id: number) => void;
  signal?: AbortSignal;
};

/**
 * `POST /stream`, read to completion.
 *
 * Failure discipline, because a reader loop that throws loses a partial answer the user has already
 * read (T-38-68): the response body is feature-detected rather than asserted, every payload is
 * decoded inside its OWN try/catch so one malformed frame is skipped instead of fatal, and the
 * caller's `onFrame` is called inside a try/catch too, because a render bug in the panel must not
 * be able to kill the transport.
 */
export const openStream = async ({
  sessionId,
  jwt,
  messageId,
  text,
  pageContext,
  lastEventId,
  onFrame,
  onId,
  signal,
}: OpenStreamInput): Promise<ChatClientResult<StreamOutcome>> => {
  if (typeof window === "undefined") return { ok: false, code: chatClientErrorMap.server_only };

  let response: Response;
  try {
    response = await window.fetch(buildUrl("/stream"), {
      method: "POST",
      headers: buildHeaders({
        sessionId,
        jwt,
        accept: "text/event-stream",
        json: true,
        lastEventId,
      }),
      credentials: "omit",
      cache: "no-store",
      body: JSON.stringify({
        messageId,
        text,
        ...(pageContext === undefined ? {} : { pageContext }),
      }),
      ...(signal === undefined ? {} : { signal }),
    });
  } catch (error) {
    return {
      ok: false,
      code: isAbortError(error) ? chatClientErrorMap.aborted : chatClientErrorMap.network_error,
    };
  }

  // `409 engine_not_streaming` lands here, and it is a REAL, EXPECTED answer rather than a bug:
  // the web channel ships resolved to v1, which cannot stream by construction, so the honest
  // refusal is the shipped default until an operator flips the setting. The panel renders its own
  // line for it and keeps the WhatsApp escape hatch visible.
  if (!response.ok) return await readFailure(response);

  const body = response.body;
  if (!body || typeof body.getReader !== "function") {
    // Some runtimes (and some proxies serving a buffered response) hand back no readable body.
    // Returning a code beats throwing: the panel can fall back to reading the thread.
    return { ok: false, code: chatClientErrorMap.stream_unsupported, status: response.status };
  }

  const reader = body.getReader();
  const decoder = new TextDecoder();
  const parser = createSseFrameParser();

  let cursor: number | null = typeof lastEventId === "number" ? lastEventId : null;
  let terminal: StreamFrameKind | null = null;
  let framesDelivered = 0;

  const dispatch = (parsed: { id?: number; data: string }): void => {
    // `onId` FIRST, per the wire contract's resume rule: the cursor the caller will send back must
    // never lag behind a frame that was already handed over, or a reconnect replays it twice.
    if (typeof parsed.id === "number") {
      cursor = parsed.id;
      try {
        onId?.(parsed.id);
      } catch {
        // A caller callback must not be able to end the stream.
      }
    }

    let frame: unknown;
    try {
      frame = JSON.parse(parsed.data);
    } catch {
      return; // One malformed frame is skipped. Framing is unaffected; the parser never parses.
    }

    const kind = (frame as { kind?: unknown } | null)?.kind;
    if (!isKnownFrameKind(kind)) return; // A kind this build does not know is not rendered.

    if (TERMINAL_KINDS.has(kind)) terminal = kind;
    framesDelivered += 1;
    try {
      onFrame(frame as WireFrame);
    } catch {
      // As above: a render bug is not a transport failure.
    }
  };

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      // Streaming decode, so a multi-byte Arabic codepoint split across two reads is reassembled
      // before the parser ever sees it. The parser handles the other half of the problem: a FRAME
      // split across two reads.
      for (const parsed of parser.push(decoder.decode(value, { stream: true }))) dispatch(parsed);
      if (terminal !== null) break; // The server deliberately does not end the response.
    }
    // Flush anything the decoder was holding.
    for (const parsed of parser.push(decoder.decode())) dispatch(parsed);
  } catch {
    // Both a local abort and a mid-stream death report what WAS received rather than discarding it.
    //
    // A local abort is not a failure: the stop button already posted to the abort endpoint, and the
    // partial the user read is theirs to keep. A mid-stream death leaves `terminal` null, which is
    // exactly the signal the resume helper reconnects on. Neither case has anything to add that
    // `terminal` and `lastEventId` do not already say, so they share one answer.
    return { ok: true, data: { lastEventId: cursor, terminal, framesDelivered } };
  } finally {
    try {
      await reader.cancel();
    } catch {
      // Already closed.
    }
  }

  return { ok: true, data: { lastEventId: cursor, terminal, framesDelivered } };
};

// ─── Resume ───────────────────────────────────────────────────────────────────

/**
 * 500ms, and the number is a UI constraint rather than a taste.
 *
 * The design contract (§ Interaction State 7) says reconnect attempts 1 and 2 must be VISUALLY
 * SILENT and that a gap over 3s is when the thinking dots reappear. With a 500ms start the first
 * retry lands at 500ms and the second at 1.5s, both inside that window, so D-09's "worst case feels
 * like a brief pause" survives. A 3000ms start would break it on the very first retry.
 */
export const RECONNECT_INITIAL_DELAY_MS = 500;

/** The ceiling. Doubling stops here rather than growing without bound. */
export const RECONNECT_MAX_DELAY_MS = 8000;

/**
 * The budget. 500 + 1000 + 2000 + 4000 + 8000 ≈ 15.5s of trying, then the panel shows its error row
 * and STOPS. An unbounded loop would be a client-side denial of service against our own dyno
 * (T-38-72).
 */
export const RECONNECT_MAX_ATTEMPTS = 5;

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/** Only a transport-level failure is worth another attempt. Every refusal is an answer. */
const isResumable = (code: ChatClientErrorCode): boolean =>
  code === chatClientErrorMap.network_error;

export type StreamWithResumeInput = OpenStreamInput & {
  /** Called when a dead session was replaced, so the caller can persist the new token. */
  onSessionRenewed?: (session: WebSession) => void;
  /** Injectable for tests; production uses `setTimeout`. */
  sleep?: (ms: number) => Promise<void>;
  maxAttempts?: number;
};

/**
 * `openStream` plus the two recoveries the wire contract asks the client to own.
 *
 * 1. A stream that ended with NO terminal frame is a drop, not an answer. Re-issue with
 *    `Last-Event-ID` set to the last id seen and the SAME `messageId`, and the server replays from
 *    the next frame and then continues live. Exponential backoff from 500ms, doubling to an 8000ms
 *    ceiling, bounded by `RECONNECT_MAX_ATTEMPTS`.
 * 2. `session_expired` / `session_unknown` gets ONE fresh mint and one retry, outside the reconnect
 *    budget. The new session is a new conversation, so the old cursor is discarded with it —
 *    resuming a stranger's offset would be worse than starting over.
 */
export const streamTurnWithResume = async ({
  onSessionRenewed,
  sleep = defaultSleep,
  maxAttempts = RECONNECT_MAX_ATTEMPTS,
  ...input
}: StreamWithResumeInput): Promise<ChatClientResult<StreamOutcome & { sessionId: string }>> => {
  let sessionId = input.sessionId;
  let cursor: number | null = input.lastEventId ?? null;
  let framesDelivered = 0;
  let mintedOnce = false;
  let attempts = 0;
  let delay = RECONNECT_INITIAL_DELAY_MS;

  for (;;) {
    const result = await openStream({
      ...input,
      sessionId,
      lastEventId: cursor,
      onId: (id) => {
        cursor = id;
        input.onId?.(id);
      },
    });

    if (!isChatClientFailure(result)) {
      framesDelivered += result.data.framesDelivered;
      if (result.data.terminal !== null) {
        return {
          ok: true,
          data: { ...result.data, framesDelivered, sessionId },
        };
      }
      // No terminal frame: the stream dropped. Fall through to the backoff.
    } else if (
      !mintedOnce &&
      (result.code === chatClientErrorMap.session_expired ||
        result.code === chatClientErrorMap.session_unknown ||
        result.code === chatClientErrorMap.session_required)
    ) {
      mintedOnce = true;
      const minted = await mintOrRefreshSession({ jwt: input.jwt, signal: input.signal });
      if (isChatClientFailure(minted)) return minted;
      sessionId = minted.data.sessionId;
      writeStoredSessionId(sessionId);
      onSessionRenewed?.(minted.data);
      cursor = null;
      continue; // Deliberately does NOT consume the reconnect budget: a different failure class.
    } else if (!isResumable(result.code)) {
      return result;
    }

    if (input.signal?.aborted) return { ok: false, code: chatClientErrorMap.aborted };

    attempts += 1;
    if (attempts >= maxAttempts) {
      return { ok: false, code: chatClientErrorMap.reconnect_exhausted };
    }

    await sleep(delay);
    delay = Math.min(delay * 2, RECONNECT_MAX_DELAY_MS);
  }
};
